import React from 'react';

interface Project {
    name: string;
    description: string;
    logo: string;
    image: string;
    url: string;
}

interface CarouselBarProps {
    projects: Project[];
    loopedProjects: Project[];
    selectedProject: Project;
    autoPlayEnabled: boolean;
    isPaused: boolean;
    progressPercent: number;
    showNavigationArrows: boolean;
    enableLoop: boolean;
    showScrollLeft: boolean;
    showScrollRight: boolean;
    isDragging: boolean;
    carouselRef: React.RefObject<HTMLDivElement | null>;
    carouselWrapperRef: React.RefObject<HTMLDivElement | null>;
    onProjectChange: (project: Project, index?: number) => void;
    onScrollCarousel: (direction: 'left' | 'right') => void;
    onTogglePause: () => void;
    onPause: () => void;
    onResume: () => void;
}

export const CarouselBar = ({
    projects,
    loopedProjects,
    selectedProject,
    autoPlayEnabled,
    isPaused,
    progressPercent,
    showNavigationArrows,
    enableLoop,
    showScrollLeft,
    showScrollRight,
    isDragging,
    carouselRef,
    carouselWrapperRef,
    onProjectChange,
    onScrollCarousel,
    onTogglePause,
    onPause,
    onResume,
}: CarouselBarProps) => {
    return (
        <div 
            className={`w-full my-[33px] relative`}
            onMouseEnter={onPause}
            onMouseLeave={onResume}
        >
            <div className="h-full flex flex-col">
                {/* Auto-play controls */}
                {autoPlayEnabled && (
                    <div className="absolute top-2 right-2 z-30 flex items-center space-x-2 p-2 bg-white bg-opacity-80 rounded-md shadow-sm hidden">
                        <button 
                            onClick={onTogglePause}
                            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                            aria-label={isPaused ? "Play" : "Pause"}
                        >
                            {isPaused ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                    <rect x="6" y="4" width="4" height="16"></rect>
                                    <rect x="14" y="4" width="4" height="16"></rect>
                                </svg>
                            )}
                        </button>
                        {/* Simple progress bar */}
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-gray-800 transition-all"
                                style={{ 
                                    width: `${progressPercent}%`,
                                    transitionProperty: isPaused ? 'none' : 'width',
                                    transitionDuration: '0.1s'
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Navigation gradients for horizontal scrolling */}
                <div className="pointer-events-none absolute left-0 top-0 h-full w-32 bg-gradient-to-r from-[#FBF8F4] to-transparent z-20" />
                <div className="pointer-events-none absolute right-0 top-0 h-full w-32 bg-gradient-to-l from-[#FBF8F4] to-transparent z-20" />

                {/* Navigation buttons for horizontal scrolling */}
                {showNavigationArrows && (enableLoop || showScrollLeft) && (
                    <button
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full shadow-md p-2"
                        onClick={() => onScrollCarousel('left')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 18l-6-6 6-6"/>
                        </svg>
                    </button>
                )}

                {/* Project carousel wrapper for reference */}
                <div 
                    ref={carouselWrapperRef} 
                    className="relative w-full overflow-hidden"
                >
                    {/* Project carousel - horizontal for all devices */}
                    <div
                        ref={carouselRef}
                        className="flex flex-row space-x-[56px] overflow-x-scroll hide-scrollbar flex-grow max-h-[200px]"
                        style={{
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            scrollBehavior: 'smooth'
                        }}
                    >
                        {loopedProjects.map((project, loopedIndex) => {
                            const originalIndex = loopedIndex % projects.length;
                            const originalProject = projects[originalIndex];
                            const isSelected = selectedProject?.name === originalProject.name;
    
                            return (
                                <button
                                    key={`${project.name}-${loopedIndex}`}
                                    onClick={(e) => {
                                        // Prevent click if currently dragging
                                        if (isDragging) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            return;
                                        }
                                        e.preventDefault();
                                        onProjectChange(originalProject, loopedIndex);
                                    }}
                                    className="flex-shrink-0 flex items-center justify-center rounded-md transition-all min-w-[120px] cursor-pointer select-none"
                                    style={{
                                        backgroundColor: isSelected ? '#1C1C1F' : 'transparent',
                                        pointerEvents: 'auto'
                                    }}
                                >
                                    <div className="text-center pointer-events-none">
                                        <p className={`h4 mx-auto ${
                                            isSelected 
                                                ? "text-white px-4 rounded-[8px]" 
                                                : "text-gray-700 py-2 rounded-[8px]"
                                        }`}>
                                            {project.name}
                                        </p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
                
                {/* Right navigation button */}
                {showNavigationArrows && (enableLoop || showScrollRight) && (
                    <button
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full shadow-md p-2"
                        onClick={() => onScrollCarousel('right')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 18l6-6-6-6"/>
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
};

export default CarouselBar;